import { Row, Col, Space, Typography, Tag, Table, Card, Spin, Alert } from 'antd';
import { EyeOutlined, SelectOutlined, RiseOutlined, SoundOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { formatMoney, formatNumber } from '../utils';
import { useDashboardInvoices, useDashboardRenewals, useDashboardAds } from '../hooks/useDashboardQueries';

const { Text } = Typography;

export const FinancialDetailsSection = ({ dateRange, isComparing, compareLabel }: any) => {
    const navigate = useNavigate();

    const params = {
        startDate: dateRange?.[0]?.format('YYYY-MM-DD'),
        endDate: dateRange?.[1]?.format('YYYY-MM-DD'),
        timezone: 'Asia/Kolkata',
        limit: 5
    };

    const { data: invoicesRes, isLoading: loadingInvoices, isError: errInvoices } = useDashboardInvoices(params);
    const { data: renewalsRes, isLoading: loadingRenewals, isError: errRenewals } = useDashboardRenewals(params);
    const { data: adsRes, isLoading: loadingAds, isError: errAds } = useDashboardAds(params);

    const invoices = invoicesRes?.data || [];
    const renewals = renewalsRes?.data || [];
    const adsPeriod = adsRes?.data?.periodMetrics;

    return (
        <Row gutter={[16, 16]}>
            <Col span={8}>
                <Card title="Recent Invoices" extra={<a href="#" onClick={(e) => { e.preventDefault(); navigate('/marketplace-invoices'); }}>View All</a>} style={{ height: '100%' }} styles={{ body: { padding: 0 } }}>
                    {loadingInvoices ? <div style={{ padding: 20, textAlign: 'center' }}><Spin /></div> : errInvoices ? <Alert type="error" message="Error loading invoices" /> : (
                        <Table
                            rowKey="id"
                            dataSource={invoices}
                            pagination={false}
                            size="small"
                            columns={[
                                { title: 'Invoice #', dataIndex: 'invoiceNumber', key: 'invoiceNumber', render: (text: string) => <Text type="secondary" style={{ fontSize: 12 }}>{text}</Text> },
                                { title: 'Merchant', dataIndex: 'merchantName', key: 'merchantName', render: (text: string) => <Text style={{ fontSize: 12 }}>{text}</Text> },
                                { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (val: any) => <Text strong style={{ fontSize: 12 }}>₹ {formatMoney(val)}</Text> },
                                { title: 'Status', dataIndex: 'status', key: 'status', render: (s: string) => <Tag color={s === 'PAID' ? 'green' : 'orange'} style={{ border: 'none', background: '#f6ffed', borderRadius: 12, fontSize: 10 }}>{s}</Tag> }
                            ]}
                        />
                    )}
                </Card>
            </Col>
            <Col span={8}>
                <Card title="Upcoming Renewals" extra={<a href="#" onClick={(e) => { e.preventDefault(); navigate('/subscriptions/dashboard'); }}>View All</a>} style={{ height: '100%' }} styles={{ body: { padding: 0 } }}>
                    {loadingRenewals ? <div style={{ padding: 20, textAlign: 'center' }}><Spin /></div> : errRenewals ? <Alert type="error" message="Error loading renewals" /> : (
                        <Table
                            rowKey="merchantId"
                            dataSource={renewals}
                            pagination={false}
                            size="small"
                            columns={[
                                { title: 'Type', dataIndex: 'type', key: 'type', render: (text: string) => <Text type="secondary" style={{ fontSize: 12 }}>{text}</Text> },
                                { title: 'Merchant', dataIndex: 'merchantName', key: 'merchantName', render: (text: string) => <Text style={{ fontSize: 12 }}>{text}</Text> },
                                { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (val: any) => <Text strong style={{ fontSize: 12 }}>₹ {formatMoney(val)}</Text> }
                            ]}
                        />
                    )}
                </Card>
            </Col>
            <Col span={8}>
                <Card title="Ad Performance" extra={<a href="#" onClick={(e) => { e.preventDefault(); navigate('/reports/ads'); }}>View Report</a>} style={{ height: '100%' }}>
                    {loadingAds ? <div style={{ padding: 20, textAlign: 'center' }}><Spin /></div> : errAds ? <Alert type="error" message="Error loading ad performance" /> : (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingTop: 8 }}>
                            <Space direction="vertical" size={2} align="center">
                                <div style={{ backgroundColor: '#f3e8ff', padding: 8, borderRadius: 8, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <EyeOutlined style={{ color: '#7c3aed', fontSize: 16 }} />
                                </div>
                                <Text type="secondary" style={{ fontSize: 11, marginTop: 4 }}>Impressions</Text>
                                <Text strong style={{ fontSize: 16, margin: 0 }}>{formatNumber(adsPeriod?.impressions)}</Text>
                            </Space>
                            <Space direction="vertical" size={2} align="center">
                                <div style={{ backgroundColor: '#e6f7ff', padding: 8, borderRadius: 8, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <SelectOutlined style={{ color: '#1890ff', fontSize: 16 }} />
                                </div>
                                <Text type="secondary" style={{ fontSize: 11, marginTop: 4 }}>Clicks</Text>
                                <Text strong style={{ fontSize: 16, margin: 0 }}>{formatNumber(adsPeriod?.clicks)}</Text>
                            </Space>
                            <Space direction="vertical" size={2} align="center">
                                <div style={{ backgroundColor: '#fff7e6', padding: 8, borderRadius: 8, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <RiseOutlined style={{ color: '#fa8c16', fontSize: 16 }} />
                                </div>
                                <Text type="secondary" style={{ fontSize: 11, marginTop: 4 }}>CTR</Text>
                                <Text strong style={{ fontSize: 16, margin: 0 }}>{adsPeriod?.ctr || 0}%</Text>
                            </Space>
                            <Space direction="vertical" size={2} align="center">
                                <div style={{ backgroundColor: '#fff0f6', padding: 8, borderRadius: 8, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <SoundOutlined style={{ color: '#eb2f96', fontSize: 16 }} />
                                </div>
                                <Text type="secondary" style={{ fontSize: 11, marginTop: 4 }}>Ad Rev</Text>
                                <Text strong style={{ fontSize: 16, margin: 0 }}>₹ {(adsPeriod?.revenue?.amount || 0) > 10000000 ? ((adsPeriod?.revenue?.amount || 0) / 10000000).toFixed(2) + 'L' : formatMoney(adsPeriod?.revenue)}</Text>
                            </Space>
                        </div>
                    )}
                </Card>
            </Col>
        </Row>
    );
};
